import { AnyObjectSchema, object, string } from "yup";
import { setIn, ValidationErrors } from "final-form";

export const wrongEmailMsg = "Email address is not valid";
const emailRequiredMsg = "Your email address is required";
const passwordLengthMsg = "Password needs to be a minimum of 6 characters";
export const fieldRequiredMsg = "Required";
export const invalidPhoneMsg = "This phone number is not valid";
export const invalidZipUsMsg = "Invalid US zip code";
export const invalidZipCaMsg = "Invalid Canadian postal code";

// Regex for English characters (includes asci 32 ` ` space to 126 `~` tilde)
// This helps to prevent users from potentially typing email address like bjørn@example.com
// It DOES NOT match for email pattern; that is done with Yup's built-in
export const regexEnglishChars = /^([\x20-\x7E]+)$/;

export const regexPhone =
  /^(\+\d{1,2}\s?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/;
// https://regexland.com/zip-codes/
export const regexZipUs = /^\d{5}(?:[- ]?\d{4})?$/;
// https://www.oreilly.com/library/view/regular-expressions-cookbook/9781449327453/ch04s15.html
export const regexZipCa =
  /^(?!.*[DdFfIiOoQqUu])[A-VXYa-vxy][0-9][A-Za-z] ?[0-9][A-Za-z][0-9]$/;

export const loginSchema = object().shape({
  email: string()
    .trim()
    .email(wrongEmailMsg)
    .matches(regexEnglishChars, {
      excludeEmptyString: true,
      message: wrongEmailMsg,
    })
    .required(emailRequiredMsg),
  password: string()
    .trim()
    .min(6, passwordLengthMsg)
    .required("Your password is required"),
});

const signUpPartial = {
  firstName: string().required(fieldRequiredMsg).nullable(),
  lastName: string().required(fieldRequiredMsg).nullable(),
  email: string()
    .trim()
    .email(wrongEmailMsg)
    .matches(regexEnglishChars, {
      excludeEmptyString: true,
      message: wrongEmailMsg,
    })
    .required(emailRequiredMsg),
  password: string()
    .trim()
    .required("Please enter a password to use for your account"),
};

export const signupSchema = object().shape({ ...signUpPartial });

export const signupWithAgeSchema = object().shape({
  ...signUpPartial,
  month: object()
    .shape({
      value: string(),
      label: string(),
    })
    .required("Please select a month")
    .nullable(),
  day: object()
    .shape({
      value: string(),
      label: string(),
    })
    .required("Please select a day")
    .nullable(),
  year: object()
    .shape({
      value: string(),
      label: string(),
    })
    .required("Please select a year")
    .nullable(),
});

export const updateNameSchema = object().shape({
  firstName: string().required(fieldRequiredMsg).nullable(),
  lastName: string().required(fieldRequiredMsg).nullable(),
});

/* https://gist.github.com/manzoorwanijk/5993a520f2ac7890c3b46f70f6818e0a */
export const validateWithYup =
  (schema: AnyObjectSchema) =>
  async (values: unknown): Promise<ValidationErrors | undefined> => {
    try {
      await schema.validate(values, { abortEarly: false });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      const errors = err?.inner?.reduce(
        (
          formError: Record<string, unknown>,
          innerError: { path: string; message: string }
        ) => {
          return setIn(formError, innerError.path, innerError.message);
        },
        {}
      );
      return errors;
    }
    return;
  };

// async field-level validation memoization from the react-final-form docs
// this is needed or the validation will infinitely loop
// https://final-form.org/docs/react-final-form/examples
// https://codesandbox.io/s/wy7z7q5zx5
// eslint-disable-next-line @typescript-eslint/ban-types
export const asyncFieldLevelMemo = (fn: Function) => {
  let lastArg: string;
  // eslint-disable-next-line @typescript-eslint/ban-types
  let lastResult: Promise<Function>;
  return (arg: string) => {
    if (arg !== lastArg) {
      lastArg = arg;
      lastResult = fn(arg);
    }
    return lastResult;
  };
};
